import React, { useState, useEffect, ChangeEvent} from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong, faImage} from '@fortawesome/free-solid-svg-icons';
import { fileToBase64, urlToFile } from '../../utils/fileHandling';
import { useAiImageTypesGeneration } from '../../hooks/useAiImageTypesGeneration';
import { aiStateTypes} from '../../types/ai/states';
import { downloadOriginal } from '../../utils/exportMedia';
import { ImageGernerationSimpleInputProps } from '../../types/imageSimple/imageGeneration';
import { getImagePixelSize } from '../../utils/images/imageConvertFormats';
import { getImageDimensions } from '../../utils/images/editFunctions';
import { ImageGenerationBodyProps, samplerList} from '../../types/ai/api';
import { handleGetRandomNumber } from '../../utils/calculations';
import { styleDescriptionPrompt } from '../../utils/aiModels/prompts';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useUpdateImageGenerationCosts } from '../../hooks/useUpdateImageGenerationCosts';
import { usePromptHook } from '../../hooks/usePromptHook';
import { parsePromptResponse } from '../../utils/chat/chatPropsParsing';
import { SettingAIOptions } from '../../components/app/generation/SettingAIOptions';
import { StatePreview } from '../../components/app/generation/StatePreview';
import { FilePlaceHolderWithDragAndDrop } from '../../components/fileupload/filePlaceholderWithDragAndDrop';
import { PromptArea } from '../../components/app/generation/PromptArea';
import { useNavigateBack } from '../../hooks/useNavigationBack';

export const ImageToImageEdit = () => {
    const { t } = useTranslation(['generation','toolsApps']);
    const isMobile = useIsMobile();
    const navigateBack = useNavigateBack();
    const [firstImg,setFirstImg] = useState<{file:any,src:string}|null>(null);
    const [generateImgSecond, setGenerateImgSecond] = useState(false);
    const [inputsCreate,setInputsCreate] = useState<ImageGernerationSimpleInputProps>({
        promptNegative:'',
        promptPositive: '',
        samplingSteps: 12,
        cfg: 2.5,
        sampler: samplerList.length > 0 ? samplerList[0] : 'Euler',
        seed: 42,
        scheduler: 'simple',
        style: 'none',
        quality: 'Standard',
        aspectRatio: '1:1 (quadratic)',
        numberOgGeneration: 1,
        reference: null
    });
    const [inputsEdit, setInputsEdit] = useState<ImageGernerationSimpleInputProps>({
        promptNegative:'',
        promptPositive: '',
        samplingSteps: 12,
        cfg: 2.5,
        sampler: samplerList.length > 0 ? samplerList[0] : 'Euler',
        seed: 42,
        scheduler: 'simple',
        style: 'none',
        quality: 'Standard',
        aspectRatio: '1:1 (quadratic)',
        numberOgGeneration: 1,
        reference: null
    });
    const [seedRandomEditActive, setSeedRandomEditActive] = useState(true);
    const [seedRandomCreateActive, setSeedRandomCreateActive] = useState(true);
    const [creationCostsCreate, updateCostsCreate] = useUpdateImageGenerationCosts();
    const [creationCostsEdit, updateCostsEdit] = useUpdateImageGenerationCosts();
    const [secondImg, setSecondImg] = useState<{file:any,src:string}|null>(null);
    const [generationIMGFirst,stateImgFirst,setStateImgFirst,getJobIDIMGFirst,insufficientCreditsDialogFirst] = useAiImageTypesGeneration();
    const [generationIMGSecond,stateImgSecond,setStateImgSecond,getJobIDIMGSecond,insufficientCreditsDialogSecond] = useAiImageTypesGeneration();
    const [generationLLM,stateLLM,setStateLLM,handleRandomPromptExternal,handleCheckPromptExternal, _,insufficientCreditsDialogLLM] = usePromptHook();
    const [aiStateFirst, setAiStateFirst] = useState<aiStateTypes>(null);
    const [aiStateSecond, setAiStateSecond] = useState<aiStateTypes>(null);
    const [aiGenerationIsRunning, setAiGenerationIsRunning] = useState(false);

    const handleRandomPrompt = async (type: 'create' | 'edit') => {
        setStateImgFirst(null);
        setStateImgSecond(null);
        setAiGenerationIsRunning(true);
        if(type === 'edit'){
            setGenerateImgSecond(true);
        }
        await handleRandomPromptExternal(type,true); 
    };
    const handleCheckPrompt = async (type: 'create' | 'edit') => {
        setStateImgFirst(null);
        setStateImgSecond(null);
        setAiGenerationIsRunning(true);
        if(type === 'edit'){
            setGenerateImgSecond(true);
        }
        await handleCheckPromptExternal(inputsEdit.promptPositive,inputsEdit.promptNegative, type);
    };
    const handleGenerationResultImageFirst = async() => {
        setAiGenerationIsRunning(false);
        let img = generationIMGFirst[0];
        let imgFile = await urlToFile(img,img.replaceAll('\\','/').split('/').slice(-1)[0]);
        setFirstImg({
            file:imgFile,
            src:img
        });
        if(seedRandomCreateActive){
            const currentInputs = {...inputsCreate};
            currentInputs.seed = handleGetRandomNumber();
            setInputsCreate(currentInputs);
        }
    };
    const handleGenerationResultImageSecond = async() => {
        setAiGenerationIsRunning(false);
        let img = generationIMGSecond[0];
        let imgFile = await urlToFile(img,img.replaceAll('\\','/').split('/').slice(-1)[0]);
        setSecondImg({
            file:imgFile,
            src:img
        });
        if(seedRandomEditActive){
            const currentInputs = {...inputsEdit};
            currentInputs.seed = handleGetRandomNumber();
            setInputsEdit(currentInputs);
        }
    };

    const generateImageFirst = async() => {
        setStateImgFirst(null);
        setStateImgSecond(null);
        setStateLLM(null);
        setAiGenerationIsRunning(true);
        setGenerateImgSecond(false);
        const imageDimension = getImagePixelSize(inputsCreate.aspectRatio, inputsCreate.quality ?? 'standard');
        const prompt = styleDescriptionPrompt(inputsCreate.promptPositive,inputsCreate.promptNegative, inputsCreate.style, false);
        const body: ImageGenerationBodyProps = {
            positive_prompt: prompt.positive_prompt, 
            negative_prompt: prompt.negative_prompt ? prompt.negative_prompt : undefined,
            width: imageDimension.width,
            height: imageDimension.height,
            seed: inputsCreate.seed,
            steps: inputsCreate.samplingSteps,
            scheduler: inputsCreate.scheduler,
            model: 'Qwen_Image',
            cfg: inputsCreate.cfg,
            number_of_images: 1,
            sampler: inputsCreate.sampler
        };
        setStateImgFirst('send');
        let path = 'generate/image'
        try {
            getJobIDIMGFirst('image_gen',path,body);
        } catch(err){
            setStateImgFirst('error');
            setAiGenerationIsRunning(false);
        };
    };
    const generateImageFromImage = async () => {
        setGenerateImgSecond(true);
        setStateImgFirst(null);
        setStateImgSecond(null);
        setStateLLM(null);
        setAiGenerationIsRunning(true);
        if(!firstImg){
            setStateImgSecond('error');
            setAiGenerationIsRunning(false);
            return;
        }
        const file = firstImg.file;
        const base64Image = await fileToBase64(file);
        const prompt = styleDescriptionPrompt(inputsEdit.promptPositive,inputsEdit.promptNegative, inputsEdit.style, false);
        const imageDimension = await getImageDimensions(file);
        const body:ImageGenerationBodyProps = {
            image: base64Image,
            positive_prompt: prompt.positive_prompt,
            negative_prompt: prompt.negative_prompt,
            width: imageDimension.width,
            height: imageDimension.height,
            seed: inputsEdit.seed,
            steps: inputsEdit.samplingSteps,
            scheduler: inputsEdit.scheduler,
            model: 'Qwen_Image_Edit`',
            cfg: inputsEdit.cfg,
            number_of_images: 1
        };
        try {
            setStateImgSecond('send');
            let path = 'generate/image-to-image-edit'
            getJobIDIMGSecond('image_gen',path,body);
        } catch(err){
            setStateImgSecond('error');
            setAiGenerationIsRunning(false);
        }
    };
    const uploadFile = async(event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0]  : null;
        if(file){
            const imgSrc = URL.createObjectURL(file);
            setFirstImg({
                'file':file,
                'src':imgSrc
            });
        }
    };
    const handleDroppedImage = (file: File) => {
        if (!file.type.startsWith("image/")) return;

        const src = URL.createObjectURL(file);
        setFirstImg({ file, src });
    };
    const download = (imgNum:'first'|'second') => {
        let resource = '';
        if (imgNum === 'first' && firstImg){
            resource = firstImg.src;
        }
        else if (imgNum === 'second' && secondImg) {
            resource = secondImg.src;
        }
        if(resource === '') return;
        const fileName = resource.replaceAll('\\','/').split('/').slice(-1)[0];
        downloadOriginal(resource, fileName)
            .catch(err => console.error('Download fehlgeschlagen', err));
    };

    const handleInputChangeCreate = (event:ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        const name = event.target.name;
        setInputsCreate(prev => ({...prev,[name]: value}));
    };

    const handleInputChangeEdit = (event:ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        const name = event.target.name;
        setInputsEdit(prev => ({...prev,[name]: value}));
    };

    useEffect(()=>{
        document.title = t('apps:registry.app.imageEdit.name')
    },[]);
    useEffect(()=>{
        if(generationIMGFirst.length > 0){
            handleGenerationResultImageFirst();
        }
    },[generationIMGFirst]);
    useEffect(()=>{
        if(generationIMGSecond.length > 0){
            handleGenerationResultImageSecond();
        }
    },[generationIMGSecond]);
    useEffect(() => {
        setAiGenerationIsRunning(false);
        setStateLLM(null);
        if (!generationLLM) return;
        const promptResponse = parsePromptResponse(generationLLM);
        if (!promptResponse) return;
        
        if(generateImgSecond){
            setGenerateImgSecond(false);
            setInputsEdit(prev => ({
                ...prev,
                prompt_positive: promptResponse.positive_prompt,
                prompt_negative: promptResponse.negative_prompt ?? ""
            }));
        }
        else{
            setInputsCreate(prev => ({
                ...prev,
                prompt_positive: promptResponse.positive_prompt,
                prompt_negative: promptResponse.negative_prompt ?? ""
            }));
        }
    }, [generationLLM]);
    useEffect(()=>{
        setGenerateImgSecond(false);
    },[firstImg]);
    useEffect(()=>{
        updateCostsCreate(inputsCreate);
    },[inputsCreate]);
    useEffect(()=>{
        updateCostsEdit(inputsEdit);
    },[inputsEdit,firstImg]);
    useEffect(()=>{
        if(stateImgFirst === 'done' || stateImgFirst === 'error' || stateImgFirst === 'unavailable' || stateLLM === 'error' || stateLLM === 'unavailable' || stateLLM === 'done' || stateImgSecond === 'error' || stateImgSecond === 'unavailable' || stateImgSecond === 'done'){
            setAiGenerationIsRunning(false);
        }
        if(stateImgFirst !== null){
            setStateLLM(null);
            setStateImgSecond(null);
            setAiStateFirst(stateImgFirst);
        }
        if(stateLLM !== null){
            setStateImgFirst(null);
            setStateImgSecond(null);
            if(generateImgSecond){
                setAiStateSecond(stateLLM);
            }
            else{
                setAiStateFirst(stateLLM);
            }
        }
        if(stateImgSecond !== null){
            setStateImgFirst(null);
            setStateLLM(null);
            setAiStateSecond(stateImgSecond)
        }
        if(!stateImgFirst && (!stateLLM && !generateImgSecond)){
            setStateImgFirst(null);
        }
        if(!stateImgSecond && (!stateLLM && generateImgSecond)){
            setStateImgSecond(null);
            setGenerateImgSecond(false);
        }

    },[stateImgFirst, stateImgSecond,stateLLM]);

  return (
    <>
        {insufficientCreditsDialogFirst}
        {insufficientCreditsDialogSecond}
        {insufficientCreditsDialogLLM}
        <div className="container-fluid h-100 pb-2">
            <div className='d-flex flex-column h-100'>
                <div className='row mx-0'>
                    <div className='col-auto p-2 pe-0'>
                        <button 
                            onClick={() => navigateBack()}
                            className="btn btn-outline-secondary btn-sm ms-3"
                            title={t('toolsApps:back')}
                        >
                        <FontAwesomeIcon
                                icon={faArrowLeftLong}
                                size="1x"
                                title={t('back')}
                            />
                        </button>
                    </div>
                    <div className='col'/>
                    <div className='col-auto h3 align-items-center'>
                        {t('editImage')}
                    </div>
                    <div className='col'/>
                </div>
                <div className={`row flex-grow-1 p-2 m-0 ${isMobile ? "overflow-y-auto overflow-x-hidden" : ""}`} style={{ minHeight: 0 }}>
                    <div className={`${!isMobile ? "col-6 h-100" : "col-12"} p-2 d-flex flex-column`}>
                        <div className='row'>
                            <div className='col h4'>
                                {t('step')} 1
                            </div>
                            <div className='col-auto h4'>
                                {t('generateImage')}
                            </div>
                            <div className='col'/>
                        </div>
                        <StatePreview state={aiStateFirst} />
                        <div className={`position-relative w-100 d-flex align-items-center justify-content-center overflow-hidden ${!isMobile ? "flex-grow-1" : ""}`}
                            style={{ minHeight: firstImg ? (isMobile ? "250px" : "350px") : "160px", maxHeight: isMobile ? "50vh" : "100%"}}
                        >
                            <FilePlaceHolderWithDragAndDrop 
                                imageSrc={ firstImg ? firstImg.src : null}
                                imageAltText={`${t('lastGeneratedImage')}: ${firstImg?.file.name}`}
                                onDownload={() => download('first')}
                                donwloadTitle={t("download.image")}
                                onDropImage={handleDroppedImage}
                                placeholderIcon={faImage}
                                placeholderText={t('noGeneratedImage')}
                            />
                        </div>
                        <div className="flex-shrink-0 mt-1">
                            <PromptArea
                                functionsDisabled={aiGenerationIsRunning}
                                credits={creationCostsCreate ?? 0}
                                onCheckPrompt={() => handleCheckPrompt('create')}
                                onRandomPrompt={() => handleRandomPrompt('create')}
                                onGenerateImage={generateImageFirst}
                                promptNegative={inputsCreate.promptNegative ?? ''}
                                promptPositive={inputsCreate.promptPositive ?? ''}
                                onInputChange={handleInputChangeCreate}
                                onUploadFile={uploadFile}
                            />
                            {   isMobile && (
                                    <SettingAIOptions
                                        aspectRatio={inputsCreate.aspectRatio}
                                        style={inputsCreate.style}
                                        sampler={inputsCreate.sampler}
                                        samplingSteps={inputsCreate.samplingSteps}
                                        seed={inputsCreate.seed}
                                        seedRandomActive={seedRandomCreateActive}
                                        setSeedRandomActive={setSeedRandomCreateActive}
                                        cfg={inputsCreate.cfg}
                                        scheduler={inputsCreate.scheduler}
                                        quality={inputsCreate.quality}
                                        onInputsChange={setInputsCreate}
                                    />
                                )
                            }
                        </div>
                    </div>
                    <div className={`${!isMobile ? "col-6 h-100" : "col-12"} p-2 d-flex flex-column`}>
                        <div className='row'>
                            <div className='col h4'>
                                {t('step')} 2
                            </div>
                            <div className='col-auto h4'>
                                {t('editImage')}
                            </div>
                            <div className='col'/>
                        </div>
                        <StatePreview state={aiStateSecond} />
                        <div className={`position-relative w-100 d-flex align-items-center justify-content-center overflow-hidden ${!isMobile ? "flex-grow-1" : ""}`}
                            style={{ minHeight: secondImg ? (isMobile ? "250px" : "350px") : "160px", maxHeight: isMobile ? "50vh" : "100%"}}
                        >
                            <FilePlaceHolderWithDragAndDrop 
                                imageSrc={ secondImg ? secondImg.src : null}
                                imageAltText={`${t('lastGeneratedImage')}: ${secondImg?.file.name}`}
                                onDownload={() => download('second')}
                                donwloadTitle={t("download.image")}
                                placeholderIcon={faImage}
                                placeholderText={t('noGeneratedImage')}
                            />
                        </div>
                        <div className="flex-shrink-0 mt-1">
                            <PromptArea
                                functionsDisabled={aiGenerationIsRunning}
                                credits={creationCostsEdit ?? 0}
                                onGenerateImage={generateImageFromImage}
                                promptPositive={inputsEdit.promptPositive}
                                promptNegative={inputsEdit.promptNegative}
                                onInputChange={handleInputChangeEdit}
                                placeholderPromptPositive={t('placeholder.positive')}
                                placeholderPromptNegative={t('placeholder.negative')}
                                onCheckPrompt={() => handleCheckPrompt('edit')}
                                onRandomPrompt={() => handleRandomPrompt('edit')}
                            />
                            {   isMobile && 
                                    <SettingAIOptions
                                        aspectRatio={inputsEdit.aspectRatio}
                                        style={inputsEdit.style}
                                        sampler={inputsEdit.sampler}
                                        samplingSteps={inputsEdit.samplingSteps}
                                        seed={inputsEdit.seed}
                                        seedRandomActive={seedRandomEditActive}
                                        setSeedRandomActive={setSeedRandomEditActive}
                                        cfg={inputsEdit.cfg}
                                        scheduler={inputsEdit.scheduler}
                                        quality={inputsEdit.quality}
                                        onInputsChange={setInputsEdit}
                                    />
                            }
                        </div>
                    </div>
                </div>
                {   !isMobile && (
                        <div className='row mx-0'>
                            <div className='col-6'>
                                <SettingAIOptions
                                    aspectRatio={inputsCreate.aspectRatio}
                                    style={inputsCreate.style}
                                    sampler={inputsCreate.sampler}
                                    samplingSteps={inputsCreate.samplingSteps}
                                    seed={inputsCreate.seed}
                                    seedRandomActive={seedRandomCreateActive}
                                    setSeedRandomActive={setSeedRandomCreateActive}
                                    cfg={inputsCreate.cfg}
                                    scheduler={inputsCreate.scheduler}
                                    quality={inputsCreate.quality}
                                    onInputsChange={setInputsCreate}
                                />
                            </div>
                            <div className='col-6'>
                                <SettingAIOptions
                                    aspectRatio={inputsEdit.aspectRatio}
                                    style={inputsEdit.style}
                                    sampler={inputsEdit.sampler}
                                    samplingSteps={inputsEdit.samplingSteps}
                                    seed={inputsEdit.seed}
                                    seedRandomActive={seedRandomEditActive}
                                    setSeedRandomActive={setSeedRandomEditActive}
                                    cfg={inputsEdit.cfg}
                                    scheduler={inputsEdit.scheduler}
                                    quality={inputsEdit.quality}
                                    onInputsChange={setInputsEdit}
                                />
                            </div>
                        </div>
                    )
                }
            </div>
        </div>
    </>
  );
};

export default ImageToImageEdit;